import React, { Dispatch, SetStateAction, useState } from 'react';
import { useMount } from 'react-use';
import { DropdownGroup, DropdownItem, FormGroup, TreeViewDataItem } from '@patternfly/react-core';
import { BuildTreeViewItem } from '../../../helpers/tree-view.helper';
import Loader from '../../util/Loader';
import SearchableTreeViewSelect, {
	dropdownToTreeViewData,
} from '../../form/Select/SearchableTreeViewSelect';
import { GenericStringKeyValueResponse } from '../../../api/types';
import { TFolder } from '../../../api/types/folder';
import _ from 'lodash';
import { buildFolderDisplayForDropdown } from '../../../helpers/dropdown-folder.helper';
import { Present, TPresent } from '../../../api/present/Present';

export type CopyPresentOptionsViewProps = {
	selectedPresent?: TPresent;
	onSelectPresent: Dispatch<SetStateAction<TPresent>>;
};

const CopyPresentOptionsView = (props: CopyPresentOptionsViewProps) => {
	const [filteredItems, setFilteredItems] = useState<TreeViewDataItem[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedPresentName, setSelectedPresentName] = useState<string>();
	const [presentations, setPresentations] = useState<TPresent[]>([]);
	const [groupedDropdownItems, setGroupedDropdownItems] = useState<JSX.Element[]>([]);

	useMount(() => {
		setIsLoading(true);

		void Present.GetAll(['folder']).then((response) => {
			setPresentations(response);

			const groupedDashboards = _.groupBy(response, 'folder.id');
			const dashboardFolderKeys = Object.keys(groupedDashboards);
			const dropdownHtml: JSX.Element[] = [];
			const dashboardFolders = response.map((x) => x.folder as TFolder);
			const kvpDashboards: GenericStringKeyValueResponse<string>[] =
				buildFolderDisplayForDropdown(dashboardFolderKeys, dashboardFolders);

			kvpDashboards.forEach((folderKey) => {
				let itemsByKey: TPresent[] = [];
				const folder = dashboardFolders.find((x) => x?.id == +folderKey.key);
				let folderName = folder?.name;

				//condition for when chart does not belong to a folder
				if (!folderName) {
					folderName = 'Unassigned';
					itemsByKey = response.filter((x) => (x?.folder as TFolder) === null);
				} else {
					itemsByKey = response.filter(
						(x) => (x?.folder as TFolder)?.id === +folderKey.key
					);
				}

				dropdownHtml.push(
					<DropdownGroup
						label={`Folder: ${folderKey.value != '' ? folderKey.value : 'Unassigned'}`}
						key={folderKey.key}
					>
						{itemsByKey.map((item) => (
							<DropdownItem
								id={item.id?.toString()}
								key={item.name}
							>
								{item.name}
							</DropdownItem>
						))}
					</DropdownGroup>
				);
			});
			setGroupedDropdownItems(dropdownHtml);

			const parsedItems: { id: number; name: string; display_name?: string | null }[] = [];

			response.map((chart) => {
				const parsedItem: { id: number; name: string; display_name?: string | null } = {
					id: chart.id ?? 0,
					name: chart.name,
				};

				parsedItems.push(parsedItem);
			});

			setFilteredItems(parsedItems.map((_) => BuildTreeViewItem(_, [])));
			setIsLoading(false);
		});
	});

	const selectPresentation = (
		_event: React.MouseEvent<Element, MouseEvent>,
		item: TreeViewDataItem
	): void => {
		const presentId = item.id?.toString() ?? '';
		const chosenPresent = presentations.find((x) => x.id == +presentId);

		if (chosenPresent) {
			props.onSelectPresent(chosenPresent);
		}

		setSelectedPresentName(item.name?.toString());
	};

	const emptyTree = [BuildTreeViewItem({ id: -1, name: 'No Presentations were found' }, [])];
	const treeViewData = dropdownToTreeViewData(groupedDropdownItems);

	return isLoading ? (
		<Loader />
	) : (
		<div data-testid="copy-view">
			<FormGroup>
				<SearchableTreeViewSelect
					className="no-innerscroll"
					data-testid="dashboard-tree"
					data={filteredItems && filteredItems.length > 0 ? treeViewData : emptyTree}
					placeHolderText={selectedPresentName ?? 'Search for a Presentation'}
					onSelect={selectPresentation}
					treeItemsExpanded={true}
					isTopLvlSearchOnly={false}
				/>
			</FormGroup>
		</div>
	);
};

export default CopyPresentOptionsView;
